import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, filter, map, skip, take } from 'rxjs';

import { LoadingService } from '@core/services/loading.service';
import { exitImpersonation, renewImpersonation, startImpersonation } from '@core/stores/auth/auth.actions';
import {
  selectImpersonationError,
  selectImpersonationIsLoading,
  selectInitialImpersonationIsLoading,
} from '@core/stores/auth/auth.selectors';

@Injectable({ providedIn: 'root' })
export class ImpersonationService {
  readonly #store = inject(Store);
  readonly #loadingService = inject(LoadingService);
  public readonly isLoading = this.#store.selectSignal(selectImpersonationIsLoading);
  public readonly error = this.#store.selectSignal(selectImpersonationError);

  public constructor() {
    this.#loadingService.addListener(this.#store.selectSignal(selectInitialImpersonationIsLoading));
  }

  public start(targetUserId: string): void {
    this.#store.dispatch(startImpersonation({ targetUserId }));
  }

  public renew$(): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      const subscription = this.#store
        .select(selectImpersonationIsLoading)
        .pipe(
          skip(1),
          filter((isLoading) => !isLoading),
          take(1),
          map(() => !!this.error())
        )
        .subscribe({
          next: (hasError) => {
            subscriber.next(!hasError);
            subscriber.complete();
          },
          error: (error: unknown) => subscriber.error(error),
        });

      this.#store.dispatch(renewImpersonation());
      return subscription;
    });
  }

  public exit(): void {
    this.#store.dispatch(exitImpersonation());
  }
}
